/**
 * LightsManager - the operator's rig: add lights, name them, address them,
 * group them, remove them.
 *
 * ## One surface, not two
 *
 * This started as a groups-only panel with the patch shown READ-ONLY and a
 * note saying editing it was a deferred decision. Phase 3 lifted that
 * deferral, and the panel absorbed it rather than gaining a sibling: a
 * second modal listing the same five lights, for a different subset of their
 * fields, would make "where do I change this?" a question the operator has to
 * answer every time. The rig is one thing.
 *
 * ## LIGHT-first, still
 *
 * The list is the rig, one row per light, and everything about a light is on
 * its row. The alternative for groups - a list of groups you tick lights into
 * - was rejected because "that one and that one are my wash" is a thought
 * about lights, not about a set, and it would ask the operator to name an
 * abstraction before they had anything to put in it. Adding the patch does
 * not change that: address and mode are also facts about a light.
 *
 * ## Make and model is browsed by ARCHETYPE
 *
 * The add flow groups OPBridge's catalogue by its seven-name archetype
 * vocabulary - Wash / Par / Bar / Mover / Beam / Strobe / Laser - because
 * that is how an operator thinks about a light before they think about its
 * brand. OPBridge added the field for exactly this reason after measuring a
 * consumer's id-regex guess wrong on 4 of 7 profiles; we are not repeating
 * that guess.
 *
 * ## The address conflict warning is the reason this phase is safe
 *
 * OPBridge checks `1..=512` and nothing else - two fixtures whose channels
 * overlap are accepted silently, and the second one's red is the first one's
 * green. See `patchStore.ts` for why the check lives here, and why it warns
 * rather than refuses.
 *
 * ## No offline draft mode, anywhere in this panel
 *
 * The rig lives on OPBridge. With no bridge the panel says so and its
 * controls are inert, because a local edit that could not be pushed would be
 * a second opinion about hardware only one of the two can see - DEC-131's
 * reasoning for never merging the two fixture lists, applied to writes.
 */

import { recordActivity } from '../navigate/recentActivity.ts';
import { createSignal, createMemo, For, Index, Show } from 'solid-js';
import type { FixtureInstance, FixtureProfile } from '@opbuttons/schema';
import { fixtures, fixtureMode } from '../fixtures/store.ts';
import { bridgeFixtureStatus, fixtureSyncLabel } from '../fixtures/bridgeSync.ts';
import { bridgeProfiles, profileSyncLabel, profileSyncStatus } from '../fixtures/profileSync.ts';
import { allGroups } from '../lighting/targets.ts';
import { addFixtureToGroup, removeFixtureFromGroup, renameGroup, deleteGroup } from '../lighting/groupStore.ts';
import {
  addFixture, editFixture, removeFixture,
  patchConflicts, describeConflicts, validateAddress,
} from '../lighting/patchStore.ts';
import { pushToast } from '../ui/toast.ts';

const [open, setOpen] = createSignal(false);
export const lightsManagerOpen = open;
export function openLightsManager(): void {
  setOpen(true);
  recordActivity({ kind: 'lights', id: 'lights', title: 'Lights' });
}
export function closeLightsManager(): void { setOpen(false); }

/** Display order for the archetype sections. Not alphabetical: it walks from
 *  the lights a small rig actually owns toward the ones it does not, so the
 *  operator's own gear is at the top of the picker. "Other" collects the
 *  profiles OPBridge declines to bucket - grouping them beats forcing them
 *  into a category that would then be wrong. */
const ARCHETYPE_ORDER = ['par', 'wash', 'bar', 'strobe', 'mover', 'beam', 'laser'] as const;
const ARCHETYPE_LABEL: Record<string, string> = {
  par: 'PAR cans', wash: 'Washes', bar: 'Bars', strobe: 'Strobes',
  mover: 'Moving heads', beam: 'Beams', laser: 'Lasers', other: 'Other',
};

export function LightsManager() {
  const [busy, setBusy] = createSignal(false);
  const [newGroupFor, setNewGroupFor] = createSignal<string | null>(null);
  const [draft, setDraft] = createSignal('');
  const [renaming, setRenaming] = createSignal<string | null>(null);
  const [renameDraft, setRenameDraft] = createSignal('');
  /** Which light's patch row is expanded. One at a time: the rig list is the
   *  thing being scanned, and four open editors would bury it. */
  const [editing, setEditing] = createSignal<string | null>(null);
  const [adding, setAdding] = createSignal(false);

  const rig = () => fixtures();
  const groups = createMemo(() => allGroups());
  const connected = () => bridgeFixtureStatus() === 'ready';

  async function run<T extends { ok: boolean; error?: string }>(
    work: () => Promise<T>,
    okMessage: string,
  ): Promise<boolean> {
    if (busy()) return false;
    setBusy(true);
    try {
      const res = await work();
      if (res.ok) pushToast('ok', okMessage, 1600);
      else pushToast('error', res.error ?? 'Could not save to OPBridge', 4000);
      return res.ok;
    } finally {
      setBusy(false);
    }
  }

  function commitNewGroup(fixtureId: string): void {
    const name = draft().trim();
    setNewGroupFor(null);
    setDraft('');
    if (!name) return;
    const f = rig().find((x) => x.id === fixtureId);
    if (!f) return;
    void run(() => addFixtureToGroup(f, name), `${f.name} added to "${name}"`);
  }

  // The section's content, shared by its own window and its pane in Navigate
  // (2026-10-05, app-study.html: every section opens inside Navigate).
  const body = () => (
    <>
          <Show when={!connected()}>
            <p class="nv-note" data-tone="warn">
              <span>{fixtureSyncLabel()}. The rig is stored on OPBridge, so it cannot be changed from here until a computer is connected.</span>
            </p>
          </Show>

          {/* ── Add a light ─────────────────────────────────────────── */}
          <Show
            when={adding()}
            fallback={
              <div class="nv-row">
                <button type="button" class="nv-b nv-b-pri" disabled={busy() || !connected()} onClick={() => setAdding(true)}>
                  Add a light
                </button>
              </div>
            }
          >
            <AddLightForm
              rig={rig()}
              busy={busy()}
              onCancel={() => setAdding(false)}
              onAdd={async (spec) => {
                const ok = await run(() => addFixture(rig(), spec), `Added "${spec.name}"`);
                if (ok) setAdding(false);
              }}
            />
          </Show>

          {/* ── The rig ─────────────────────────────────────────────── */}
          <section class="nv-ss">
            <div class="nv-ss-h"><h2>Your lights</h2><p>Tap Edit to change where one is patched. Tag lights into a group and one control can aim at all of them.</p></div>
            <Show when={rig().length > 0} fallback={<p class="nav-empty">{fixtureSyncLabel()}</p>}>
              <div class="nav-well">
                <Index each={rig()}>
                  {(f) => {
                    const mode = () => fixtureMode(f());
                    const channels = () => mode()?.channelCount ?? 0;
                    const last = () => (channels() ? f().dmxAddress + channels() - 1 : f().dmxAddress);
                    /** Live overlap warning on the row itself, not only inside the
                     *  editor - an overlap created by editing a DIFFERENT light has
                     *  to be visible without opening this one. */
                    const clash = () => describeConflicts(
                      patchConflicts(rig(), { universe: f().universe, dmxAddress: f().dmxAddress, channelCount: channels() }, f().id),
                    );
                    return (
                      <div class="nav-lr light-row" data-clash={clash() ? 'true' : undefined}>
                        <span class="nav-lr-main">
                          <b>{f().name}</b>
                          <small>
                            {f().mode} · universe {f().universe} · channel{channels() > 1 ? 's' : ''} {f().dmxAddress}
                            {channels() > 1 ? `-${last()}` : ''}
                          </small>
                          <Show when={clash()}>
                            <small class="light-warn">{clash()}</small>
                          </Show>
                          <span class="light-tags">
                            <For each={f().groups ?? []}>
                              {(g) => (
                                <button
                                  type="button" class="nv-ct light-tag" disabled={busy() || !connected()}
                                  onClick={() => void run(
                                    () => removeFixtureFromGroup(f(), g),
                                    `${f().name} removed from "${g}"`,
                                  )}
                                  title={`Take ${f().name} out of "${g}"`}
                                >
                                  {g} &times;
                                </button>
                              )}
                            </For>
                          </span>

                          <Show when={editing() === f().id}>
                            <EditLightForm
                              fixture={f()}
                              rig={rig()}
                              busy={busy()}
                              onCancel={() => setEditing(null)}
                              onSave={async (patch) => {
                                const ok = await run(() => editFixture(f(), patch), `Saved "${patch.name ?? f().name}"`);
                                if (ok) setEditing(null);
                              }}
                              onDelete={async () => {
                                const ok = await run(() => removeFixture(f()), `Deleted "${f().name}"`);
                                if (ok) setEditing(null);
                              }}
                            />
                          </Show>
                        </span>

                        <span class="nav-lr-trail light-actions">
                          <Show when={newGroupFor() === f().id} fallback={
                            <>
                              <For each={groups().filter((g: string) => !(f().groups ?? []).includes(g))}>
                                {(g) => (
                                  <button
                                    type="button" class="nv-b nv-b-q nv-b-sm" disabled={busy() || !connected()}
                                    onClick={() => void run(
                                      () => addFixtureToGroup(f(), g),
                                      `${f().name} added to "${g}"`,
                                    )}
                                    title={`Add ${f().name} to "${g}"`}
                                  >
                                    + {g}
                                  </button>
                                )}
                              </For>
                              <button
                                type="button" class="nv-b nv-b-q nv-b-sm" disabled={busy() || !connected()}
                                onClick={() => { setNewGroupFor(f().id); setDraft(''); }}
                              >
                                New group
                              </button>
                              {/* Hidden while this light is being edited: the form's own
                                  Save and Cancel are the only two ways out, so nobody
                                  has to guess whether "Done" saves (it did not). */}
                              <Show when={editing() !== f().id}>
                                <button
                                  type="button" class="nv-b nv-b-sm" disabled={busy() || !connected()}
                                  onClick={() => setEditing(f().id)}
                                >
                                  Edit
                                </button>
                              </Show>
                            </>
                          }>
                            <span class="nv-fld light-newgroup">
                              <input
                                type="text"
                                placeholder="Name the group"
                                value={draft()}
                                autofocus
                                onInput={(e) => setDraft(e.currentTarget.value)}
                                onBlur={() => commitNewGroup(f().id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') { setNewGroupFor(null); setDraft(''); return; }
                                  if (e.key === 'Enter') commitNewGroup(f().id);
                                }}
                              />
                            </span>
                          </Show>
                        </span>
                      </div>
                    );
                  }}
                </Index>
              </div>
            </Show>
          </section>

          {/* ── Groups ──────────────────────────────────────────────── */}
          <Show when={groups().length > 0}>
            <section class="nv-ss">
              <div class="nv-ss-h"><h2>Groups</h2><p>Renaming or deleting a group never touches the lights in it.</p></div>
              <div class="nav-well">
                <Index each={groups()}>
                  {(g) => (
                    <div class="nav-lr">
                      <span class="nav-lr-main">
                        <Show
                          when={renaming() === g()}
                          fallback={
                            <>
                              <b>{g()}</b>
                              <small>
                                {(() => { const n = rig().filter((f) => (f.groups ?? []).includes(g())).length; return `${n} light${n === 1 ? '' : 's'}`; })()}
                              </small>
                            </>
                          }
                        >
                          <span class="nv-fld">
                            <input
                              type="text"
                              value={renameDraft()}
                              autofocus
                              aria-label="Group name"
                              onInput={(e) => setRenameDraft(e.currentTarget.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') { setRenaming(null); return; }
                                if (e.key !== 'Enter') return;
                                const to = renameDraft().trim();
                                setRenaming(null);
                                if (!to || to === g()) return;
                                void run(
                                  async () => {
                                    const r = await renameGroup(rig(), g(), to);
                                    return { ok: r.ok, ...(r.error ? { error: r.error } : {}) };
                                  },
                                  `Renamed "${g()}" to "${to}"`,
                                );
                              }}
                            />
                          </span>
                        </Show>
                      </span>
                      <span class="nav-lr-trail">
                        <button
                          type="button" class="nv-b nv-b-q nv-b-sm" disabled={busy() || !connected()}
                          onClick={() => { setRenaming(g()); setRenameDraft(g()); }}
                        >
                          Rename
                        </button>
                        <button
                          type="button" class="nv-b nv-b-q nv-b-sm" data-danger="true" disabled={busy() || !connected()}
                          onClick={() => void run(
                            async () => {
                              const r = await deleteGroup(rig(), g());
                              return { ok: r.ok, ...(r.error ? { error: r.error } : {}) };
                            },
                            `Deleted group "${g()}"`,
                          )}
                          title="Takes the tag off every light. The lights themselves are untouched."
                        >
                          Delete
                        </button>
                      </span>
                    </div>
                  )}
                </Index>
              </div>
            </section>
          </Show>
    </>
  );
  return <div class="nav-pane-body" role="region" aria-label="Lights">{body()}</div>;
}

/* ── Add ──────────────────────────────────────────────────────────────── */

interface AddSpec {
  name: string;
  profile: FixtureProfile;
  mode: string;
  universe: number;
  dmxAddress: number;
}

function AddLightForm(props: {
  rig: FixtureInstance[];
  busy: boolean;
  onAdd: (spec: AddSpec) => void;
  onCancel: () => void;
}) {
  const catalogue = () => Object.values(bridgeProfiles());
  const [profileId, setProfileId] = createSignal('');
  const [modeName, setModeName] = createSignal('');
  const [name, setName] = createSignal('');
  const [universe, setUniverse] = createSignal(1);
  const [address, setAddress] = createSignal(1);

  const profile = () => catalogue().find((p) => p.id === profileId());
  const mode = () => profile()?.modes.find((m) => m.name === modeName()) ?? profile()?.modes[0];
  const channels = () => mode()?.channelCount ?? 0;

  /** Grouped by archetype, in ARCHETYPE_ORDER, with everything OPBridge
   *  declines to bucket collected under "Other" rather than forced. */
  const grouped = createMemo(() => {
    const byKey = new Map<string, FixtureProfile[]>();
    for (const p of catalogue()) {
      const key = p.archetype && ARCHETYPE_ORDER.includes(p.archetype) ? p.archetype : 'other';
      const list = byKey.get(key) ?? [];
      list.push(p);
      byKey.set(key, list);
    }
    const order = [...ARCHETYPE_ORDER, 'other'];
    return order
      .filter((k) => byKey.has(k))
      .map((k) => ({ key: k, label: ARCHETYPE_LABEL[k] ?? k, profiles: (byKey.get(k) ?? []).sort((a, b) => a.name.localeCompare(b.name)) }));
  });

  const rangeError = () => (channels() ? validateAddress(address(), channels()) : undefined);
  const clash = () => (channels()
    ? describeConflicts(patchConflicts(props.rig, { universe: universe(), dmxAddress: address(), channelCount: channels() }))
    : undefined);

  /**
   * Where this light WOULD fit, offered rather than auto-applied: an operator
   * patching to match DIP switches already set on the fixture itself must not
   * have the app quietly move it.
   *
   * Scans UP from the address being typed, and only falls back to scanning
   * from 1 if nothing fits above. The naive version scanned from 1 always, and
   * against the real rig - which starts at 31 - it answered "first free
   * address is 1" to an operator typing 31. True, and useless: they were
   * telling us roughly where the light is, and the answer sent them to the
   * other end of the universe. The right answer there is 39.
   *
   * Returns which KIND of answer it is, because "the next one above where you
   * are" and "the only space left is back at the start" are different pieces
   * of advice and the operator should be able to tell them apart.
   */
  const suggestion = (): { at: number; above: boolean } | undefined => {
    const n = channels();
    if (!n) return undefined;
    const fits = (a: number) => a + n - 1 <= 512
      && patchConflicts(props.rig, { universe: universe(), dmxAddress: a, channelCount: n }).length === 0;
    for (let a = address() + 1; a + n - 1 <= 512; a += 1) {
      if (fits(a)) return { at: a, above: true };
    }
    for (let a = 1; a < address(); a += 1) {
      if (fits(a)) return { at: a, above: false };
    }
    return undefined;
  };

  return (
    <section class="nv-ss">
      <div class="nv-ss-h"><h2>Add a light</h2></div>
      <div class="nav-well nv-well-pad">
      <div class="nv-form">

        <Show
          when={catalogue().length > 0}
          fallback={<p class="nv-note" data-tone="warn"><span>{profileSyncLabel()}</span></p>}
        >
          <label class="nv-field nv-form-wide">
            <span>What is it</span>
            <select
              class="nv-select"
              value={profileId()}
              onChange={(e) => {
                setProfileId(e.currentTarget.value);
                const p = catalogue().find((x) => x.id === e.currentTarget.value);
                setModeName(p?.modes[0]?.name ?? '');
                if (!name().trim() && p) setName(p.model);
              }}
            >
              <option value="">Pick a make and model</option>
              <For each={grouped()}>
                {(section) => (
                  <optgroup label={section.label}>
                    <For each={section.profiles}>
                      {(p) => <option value={p.id}>{p.name}</option>}
                    </For>
                  </optgroup>
                )}
              </For>
            </select>
          </label>
        </Show>

        {/* Mode only appears when the profile HAS a choice. A single-mode
            fixture should not make the operator confirm its only option. */}
        <Show when={(profile()?.modes.length ?? 0) > 1}>
          <label class="nv-field">
            <span>Mode</span>
            <select class="nv-select" value={modeName()} onChange={(e) => setModeName(e.currentTarget.value)}>
              <For each={profile()?.modes ?? []}>
                {(m) => <option value={m.name}>{m.name} ({m.channelCount} ch)</option>}
              </For>
            </select>
          </label>
        </Show>

        <Show when={profile()}>
          <>
            <label class="nv-field">
              <span>Call it</span>
              <input
                type="text"
                placeholder="Back Left"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </label>
            <label class="nv-field">
              <span>Universe</span>
              <input
                type="number" min="1" max="255"
                value={universe()}
                onInput={(e) => setUniverse(Math.max(1, Number(e.currentTarget.value) || 1))}
              />
            </label>
            <label class="nv-field">
              <span>Address</span>
              <input
                type="number" min="1" max="512"
                value={address()}
                onInput={(e) => setAddress(Math.max(1, Number(e.currentTarget.value) || 1))}
              />
            </label>
            <span class="nv-form-note">
              Uses {channels()} channel{channels() === 1 ? '' : 's'}: {address()}
              {channels() > 1 ? `-${address() + channels() - 1}` : ''}
            </span>
            <Show when={rangeError()}>
              <span class="nv-form-note light-warn">{rangeError()}</span>
            </Show>
            <Show when={clash()}>
              {/* ACTIONABLE PART FIRST. The operator can do something with
                  "first free address is 39"; "two lights on one address is
                  legal" is reassurance. Ordered so that if this line is ever
                  clipped again, what is lost is the half that does not
                  matter. */}
              <span class="nv-form-note light-warn">
                {clash()}.
                {(() => {
                  const s = suggestion();
                  if (!s) return '';
                  return s.above
                    ? ` Next clear address is ${s.at}.`
                    : ` Nothing clear above this - the first free address is ${s.at}.`;
                })()}
                {' '}Allowed either way - two lights on one address is sometimes deliberate.
              </span>
            </Show>
          </>
        </Show>
      </div>
      <div class="nv-row">
      <button
        type="button" class="nv-b nv-b-pri"
        disabled={props.busy || !profile() || !mode() || !!rangeError() || !name().trim()}
        onClick={() => {
          const p = profile();
          const m = mode();
          if (!p || !m) return;
          props.onAdd({ name: name(), profile: p, mode: m.name, universe: universe(), dmxAddress: address() });
        }}
      >
        Add the light
      </button>
      <button type="button" class="nv-b nv-b-q" disabled={props.busy} onClick={() => props.onCancel()}>
        Cancel
      </button>
      </div>
      </div>
    </section>
  );
}

/* ── Edit ─────────────────────────────────────────────────────────────── */

function EditLightForm(props: {
  fixture: FixtureInstance;
  rig: FixtureInstance[];
  busy: boolean;
  onSave: (patch: Partial<Pick<FixtureInstance, 'name' | 'mode' | 'universe' | 'dmxAddress'>>) => void;
  onDelete: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = createSignal(props.fixture.name);
  const [modeName, setModeName] = createSignal(props.fixture.mode);
  const [universe, setUniverse] = createSignal(props.fixture.universe);
  const [address, setAddress] = createSignal(props.fixture.dmxAddress);
  const [confirmDelete, setConfirmDelete] = createSignal(false);

  const profile = () => bridgeProfiles()[props.fixture.profileId];
  const mode = () => profile()?.modes.find((m) => m.name === modeName());
  const channels = () => mode()?.channelCount ?? fixtureMode(props.fixture)?.channelCount ?? 0;

  const rangeError = () => (channels() ? validateAddress(address(), channels()) : undefined);
  const clash = () => (channels()
    ? describeConflicts(patchConflicts(
        props.rig,
        { universe: universe(), dmxAddress: address(), channelCount: channels() },
        props.fixture.id,
      ))
    : undefined);

  return (
    <span class="nv-form light-edit">
      <label class="nv-field">
        <span>Name</span>
        <input type="text" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
      </label>
      {/* The make and model is NOT editable. Changing it would change what
          every channel means, which is a different light, not an edit - add
          the new one and remove this. Shown so the row still identifies the
          fixture. */}
      <span class="nv-form-note">{profile()?.name ?? props.fixture.profileId}</span>
      <Show when={(profile()?.modes.length ?? 0) > 1}>
        <label class="nv-field">
          <span>Mode</span>
          <select class="nv-select" value={modeName()} onChange={(e) => setModeName(e.currentTarget.value)}>
            <For each={profile()?.modes ?? []}>
              {(m) => <option value={m.name}>{m.name} ({m.channelCount} ch)</option>}
            </For>
          </select>
        </label>
      </Show>
      <label class="nv-field">
        <span>Universe</span>
        <input
          type="number" min="1" max="255"
          value={universe()}
          onInput={(e) => setUniverse(Math.max(1, Number(e.currentTarget.value) || 1))}
        />
      </label>
      <label class="nv-field">
        <span>Address</span>
        <input
          type="number" min="1" max="512"
          value={address()}
          onInput={(e) => setAddress(Math.max(1, Number(e.currentTarget.value) || 1))}
        />
      </label>
      <Show when={rangeError()}>
        <span class="nv-form-note light-warn">{rangeError()}</span>
      </Show>
      <Show when={clash()}>
        <span class="nv-form-note light-warn">{clash()}</span>
      </Show>

      <span class="nv-row nv-form-wide">
        <button
          type="button" class="nv-b nv-b-pri nv-b-sm"
          disabled={props.busy || !!rangeError() || !name().trim()}
          onClick={() => props.onSave({
            name: name(), mode: modeName(), universe: universe(), dmxAddress: address(),
          })}
        >
          Save
        </button>
        <button type="button" class="nv-b nv-b-q nv-b-sm" disabled={props.busy} onClick={() => props.onCancel()}>
          Cancel
        </button>
        {/* Two taps to delete, and the second one says what it removes.
            Removing a fixture tombstones it across the whole mesh and takes
            every control aimed at it dead with it - that is not a thing to
            do on one mis-tap in a dark room. */}
        <Show
          when={confirmDelete()}
          fallback={
            <button type="button" class="nv-b nv-b-q nv-b-sm" data-danger="true" disabled={props.busy} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          }
        >
          <button
            type="button" class="nv-b nv-b-q nv-b-sm" data-danger="armed"
            disabled={props.busy}
            onClick={() => props.onDelete()}
            title="Deletes it from the rig on every machine"
          >
            Really delete {props.fixture.name}?
          </button>
        </Show>
      </span>
    </span>
  );
}
